import { apiSupportedModels } from '@modules/aawz-intelligence-v2/api/apiSupportedModels'
import {
  ID_SEM_MODELO,
  modeloValidoOuPadrao,
} from '@modules/aawz-intelligence-v2/utils/modelos-suportados'
import {
  criarStateToolParts,
  toolPartsActions,
  toolPartsGetters,
  toolPartsMutations,
} from '@modules/aawz-intelligence-v2/store/toolParts'
// ★ Badges multi-agente (D6/RF-G2 da spec-010 do hermes). O slice é o MESMO do
// chat do CRM, e tem de estar aqui porque quem despacha é o motor comum: o
// `APIChat` manda `<namespace>/setHubDaConversa` (e as duas irmãs) por
// `this.storeNamespace`, que aqui é `feeFixoIa`. Sem a composição, cada turno
// do drawer levantava três `[vuex] unknown action type` — medido no app real em
// 2026-09-04 — e nenhum badge aparecia. Ver `store/superficies-do-chat.unit.js`,
// a junção que trava dispatch × store nas três superfícies.
import {
  criarStateHubEAgente,
  hubEAgenteActions,
  hubEAgenteGetters,
  hubEAgenteMutations,
} from '@modules/aawz-intelligence-v2/store/hub-e-agente'
import * as deposito from '@modules/fee-fixo/store/ia-deposito'

// O namespace com que `index.js` registra este slice — a API persiste nele. Literal aqui,
// e não o `FEE_FIXO_IA_STORE` de `api/apiFeeFixoIa.js`: importar a classe do chat traria o
// motor SSE inteiro para dentro do store.
const NAMESPACE = 'feeFixoIa'
// Chaves PRÓPRIAS de localStorage. A preferência de modelo do popup do CRM
// (`iaCrmSelectedModel`) não vale aqui: são dois painéis que o consultor abre
// em contextos diferentes, e reaproveitar a chave faria a escolha feita num
// deles mudar o outro sem que ninguém tivesse pedido.
const LS_MODELO = 'feeFixoIaSelectedModel'
const LS_THINKING = 'feeFixoIaThinkingEnabled'

function lerLocalStorage(chave, padrao) {
  try {
    const guardado = localStorage.getItem(chave)
    return guardado === null ? padrao : guardado
  } catch (e) {
    console.warn('[fee-fixo-ia] leitura de ' + chave + ' falhou — usando o padrão', e)
    return padrao
  }
}

function gravarLocalStorage(chave, valor) {
  try {
    localStorage.setItem(chave, valor)
  } catch (e) {
    console.warn('[fee-fixo-ia] gravação de ' + chave + ' falhou', e)
  }
}

/**
 * Store do Especialista Fee-Based (drawer de IA das telas de Fee Fixo).
 *
 * Namespace PRÓPRIO, separado do `aawz-intelligence-v2`, de propósito:
 * `selectedConversation` e `messages` moram no store, então compartilhar o
 * namespace faria uma pergunta feita no drawer ser anexada à conversa que o
 * usuário tem aberta no chat do CRM — e com a persona errada no histórico.
 *
 * O shape das actions é o que o `APIChat` despacha (ver
 * `@modules/aawz-intelligence-v2/api/apiChat`): mudar nome aqui quebra o
 * streaming em silêncio.
 */


export const state = () => ({
  // Visibilidade do drawer. Mora aqui, e não no `data()` do componente, porque
  // `src/app.vue` amarra a `key` do RouterView ao `$route.fullPath`: cada troca
  // de aba destrói e recria o componente, e um `aberto` local fecharia o painel
  // na cara do consultor a cada navegação — com a conversa intacta por baixo.
  // (O número de linha citado aqui antes já tinha envelhecido — hoje é a 82. O
  // que identifica é o binding. A MESMA key morde no eixo da QUERY: ver a
  // armadilha "navegação que só muda a query remonta o módulo" no §14 do
  // CLAUDE.md deste módulo.)
  painelAberto: false,
  messages: [],
  chatLoading: false,
  responseTokens: [],
  thinkingSteps: [],
  toolCalls: [],
  pendingConfirmation: null,
  // Filtro que a tool `sugerir_filtro_clientes` devolveu (spec-012): a aba
  // Clientes lê daqui para montar o preview. Guarda a RECUSA também — filtro
  // barrado que sumisse em silêncio deixaria o consultor esperando.
  filtroSugerido: null,
  /**
   * O preview da simulação criada pela conversa (spec-007, [T108]).
   *
   * Guardado à parte de `pendingConfirmation` porque a superfície é outra: as
   * duas escritas anteriores usam o card genérico de confirmação, e esta usa o
   * cartão que CALCULA e desenha os componentes do Fee Lab. Reaproveitar a
   * mesma pendência faria o card genérico tentar montar itens que este preview
   * não tem — e o consultor veria uma confirmação sem os números.
   *
   * `null` = nenhum preview em aberto. Um AJUSTE substitui o valor inteiro:
   * dois cartões com `previewId` diferentes na tela é o consultor confirmando
   * o que não está olhando.
   */
  simulacaoPendente: null,
  // O rascunho que a tela do Fee Lab publica (spec-014): as premissas e os
  // resultados que o engine acabou de calcular, ainda não salvos. Mora aqui
  // porque quem PERGUNTA é o drawer — que é global — e quem tem o estado é a
  // view da simulação. Fica `null` fora do Fee Lab: mandar o rascunho de uma
  // tela que o consultor já deixou seria pior que não mandar nada.
  rascunhoDaTela: null,
  // O que a tool `analisar_rascunho_simulacao` devolveu: a leitura do cenário e
  // o que mudaria o resultado — sem número, por construção do backend.
  rascunhoAnalisado: null,
  selectedConversation: null,
  // ── Lateral de conversas do guia (spec-005, [T004]) ─────────────────────
  //
  // A lista do profissional nesta persona, como o backend a devolve. Ela é do
  // PROFISSIONAL, não da conversa aberta: "Nova conversa" zera o thread e
  // preserva a lateral.
  conversations: [],
  conversationsLoading: false,
  // Booleano de propósito — a lateral só precisa saber que falhou, para
  // oferecer "tentar de novo" em vez de "nenhuma conversa ainda" (US-3). Mesma
  // semântica do slice do intelligence-v2, que é quem renderiza a coluna.
  conversationsError: false,
  // O cliente da conversa ABERTA (`{ idClienteApp, codigo, nome }`) ou null na
  // conversa geral. Mora aqui, e não na URL: `src/app.vue:82` keya o
  // `RouterView` por `$route.fullPath`, então trocar de conversa por query
  // remontaria o módulo inteiro — e mataria qualquer modal aberto (D7).
  clienteDaConversa: null,
  // Os clientes que o TURNO identificou (spec-006, [T008]) — o que o cartão de
  // atalho do thread renderiza. Lista vazia = nenhum cartão.
  //
  // ★ [G-F4] ACUMULA dentro do turno: um turno pode chamar a tool de estado N
  // vezes (um cliente por chamada), e substituir deixava o cartão com UMA
  // linha depois de a IA nomear cinco. Entre turnos ele zera — quem zera é o
  // `resetChatState` da classe de API ([G-F1]). As duas metades são
  // inseparáveis: acumular sem zerar empilharia os citados da conversa toda.
  //
  // Mora aqui, e não no `data()` da view, porque quem os recebe é a classe de
  // API, no `tool_result` do stream — o mesmo caminho do recorte sugerido.
  clientesCitados: [],
  // ★ [G-A6] A mensagem do servidor quando o TURNO falhou — a recusa de
  // escrever numa conversa removida é o caso que a nomeou. Vazio = sem erro.
  //
  // Mora na store, e não no `data()` da view, porque quem a recebe é a classe
  // de API (`apiFeeFixoIa._onEventError`), que não tem componente: o evento SSE
  // `error` chega DENTRO do stream, e o `sendMessage` devolve `{}` sem `status`
  // — o `$showSnack` da view nunca dispara. Sem este slice, a mensagem parava
  // no `console.error` e o consultor via a própria bolha e mais nada.
  erroDoTurno: '',
  // As partes de tool do turno (spec-001 [T006]). Estado PRÓPRIO deste
  // namespace — `criarStateToolParts()` devolve mapa e ordem novos, e é o que
  // impede o drawer de mostrar os cartões do chat do CRM.
  ...criarStateToolParts(),
  // Hub do turno e agentes que responderam (D6/RF-G2). Estado PRÓPRIO deste
  // namespace, pela mesma razão das partes de tool: `agentePorMensagem` é um
  // mapa, e compartilhá-lo faria o badge de uma mensagem do chat do CRM
  // aparecer sobre a resposta do drawer.
  ...criarStateHubEAgente(),
  ...deposito.criarStateDeposito(),

  // Seleção de modelo. O `SendMessage` compartilhado lê estes três campos
  // por `$store.state[storeModule]`, então os nomes são contrato — mudá-los
  // apaga o seletor sem erro nenhum.
  // ★ SEM literal de id de modelo (Q7-A08 / K5, 03/09/2026). Vazio até a lista
  // do backend chegar; quem aplica o padrão e descarta um id obsoleto é o
  // `SET_SUPPORTED_MODELS`. A chave de `localStorage` continua sendo a PRÓPRIA
  // do drawer — o que muda é só de onde sai o valor quando não há escolha.
  selectedModel: lerLocalStorage(LS_MODELO, ID_SEM_MODELO),
  supportedModels: [],
  supportedModelsLoaded: false,
  thinkingEnabled: lerLocalStorage(LS_THINKING, 'true') !== 'false',
})

/** Conversa sem cliente é a da visão gerencial. `0`/`''` são o mesmo fato. */
const semCliente = (conversa) => !conversa || !conversa.id_cliente_app

export const getters = {
  ...toolPartsGetters,
  ...hubEAgenteGetters,
  ...deposito.depositoGetters,
  painelAberto: (state) => !!state.painelAberto,
  messages: (state) => state.messages || [],
  chatLoading: (state) => !!state.chatLoading,
  // String única — o componente renderiza o texto que está chegando token a token.
  responseTokens: (state) => (state.responseTokens || []).join(''),
  thinkingSteps: (state) => state.thinkingSteps || [],
  toolCalls: (state) => state.toolCalls || [],
  pendingConfirmation: (state) => state.pendingConfirmation,
  filtroSugerido: (state) => state.filtroSugerido,
  simulacaoPendente: (state) => state.simulacaoPendente,
  rascunhoDaTela: (state) => state.rascunhoDaTela,
  rascunhoAnalisado: (state) => state.rascunhoAnalisado,
  selectedConversation: (state) => state.selectedConversation,
  conversationId: (state) =>
    state.selectedConversation && state.selectedConversation.id
      ? state.selectedConversation.id
      : null,

  // ── Lateral de conversas do guia (spec-005, [T004]) ─────────────────────
  conversations: (state) => state.conversations || [],
  conversationsLoading: (state) => !!state.conversationsLoading,
  conversationsError: (state) => Boolean(state.conversationsError),
  clienteDaConversa: (state) => state.clienteDaConversa,
  erroDoTurno: (state) => state.erroDoTurno || '',
  clientesCitados: (state) => state.clientesCitados || [],

  /**
   * A conversa geral — a mais recente SEM cliente, ou null.
   *
   * O v0 dá à visão gerencial UM item na lateral (spec §US-3, "item único"); o
   * backend devolve a listagem ordenada por `created_at DESC`, então a primeira
   * sem cliente é a mais recente. Conversas gerais mais antigas continuam no
   * servidor e são alcançáveis pela retomada — o que o v0 não faz é dar uma
   * linha a cada uma.
   */
  conversaGeral: (state) => (state.conversations || []).find(semCliente) || null,

  /**
   * Uma entrada por conversa vinculada a cliente.
   *
   * A separação vive aqui, e não no componente, porque as duas listas saem da
   * MESMA resposta: dois `filter` espalhados pela tela seriam duas noções do
   * que é "conversa de cliente", divergindo no primeiro payload malformado.
   */
  conversasDeCliente: (state) =>
    (state.conversations || []).filter((conversa) => !semCliente(conversa)),

  // Sem fallback literal: vazio significa "a lista ainda não chegou", e quem
  // envia o turno manda `null` — o backend aplica o padrão dele.
  selectedModel: (state) => state.selectedModel || ID_SEM_MODELO,
  supportedModels: (state) => state.supportedModels || [],
  // Quem consome é o toggle de thinking do composer: ele só aparece para
  // modelo cujo provider suporta, e é este getter que diz qual é o ativo.
  currentModelMeta: (state) => {
    const modelos = state.supportedModels || []
    for (const modelo of modelos) {
      if (modelo.id === state.selectedModel) return modelo
    }
    return null
  },
}

export const mutations = {
  ...toolPartsMutations,
  ...hubEAgenteMutations,
  ...deposito.depositoMutations,
  SET_PAINEL_ABERTO: (state, payload) => {
    state.painelAberto = !!payload
  },
  SET_MESSAGES: (state, payload) => {
    state.messages = payload || []
  },
  ADD_MESSAGE: (state, payload) => {
    state.messages = [...state.messages, payload]
  },
  SET_CHAT_LOADING: (state, payload) => {
    state.chatLoading = !!payload
  },
  ADD_RESPONSE_TOKENS: (state, payload) => {
    state.responseTokens = [...state.responseTokens, ...(payload || [])]
  },
  ADD_RESPONSE_TOKEN: (state, payload) => {
    state.responseTokens = [...state.responseTokens, payload]
  },
  RESET_RESPONSE_TOKENS: (state) => {
    state.responseTokens = []
  },
  ADD_THINKING_STEP: (state, payload) => {
    state.thinkingSteps = [...state.thinkingSteps, payload]
  },
  RESET_THINKING_STEPS: (state) => {
    state.thinkingSteps = []
  },
  ADD_TOOL_CALL: (state, payload) => {
    state.toolCalls = [...state.toolCalls, payload]
  },
  REMOVE_TOOL_CALL: (state, toolName) => {
    const idx = state.toolCalls.indexOf(toolName)
    if (idx === -1) return
    state.toolCalls = state.toolCalls.filter((_, i) => i !== idx)
  },
  RESET_TOOL_CALLS: (state) => {
    state.toolCalls = []
  },
  SET_PENDING_CONFIRMATION: (state, payload) => {
    state.pendingConfirmation = payload
  },
  RESET_PENDING_CONFIRMATION: (state) => {
    state.pendingConfirmation = null
  },
  SET_FILTRO_SUGERIDO: (state, payload) => {
    state.filtroSugerido = payload
  },
  RESET_FILTRO_SUGERIDO: (state) => {
    state.filtroSugerido = null
  },
  SET_SIMULACAO_PENDENTE: (state, payload) => {
    state.simulacaoPendente = payload
  },
  RESET_SIMULACAO_PENDENTE: (state) => {
    state.simulacaoPendente = null
  },
  SET_RASCUNHO_DA_TELA: (state, payload) => {
    state.rascunhoDaTela = payload || null
  },
  SET_RASCUNHO_ANALISADO: (state, payload) => {
    state.rascunhoAnalisado = payload
  },
  RESET_RASCUNHO_ANALISADO: (state) => {
    state.rascunhoAnalisado = null
  },
  SET_SELECTED_CONVERSATION: (state, payload) => {
    state.selectedConversation = payload
  },
  SET_CONVERSATIONS: (state, payload) => {
    state.conversations = payload || []
  },
  SET_CONVERSATIONS_LOADING: (state, payload) => {
    state.conversationsLoading = !!payload
  },
  SET_CONVERSATIONS_ERROR: (state, payload) => {
    state.conversationsError = !!payload
  },
  SET_CLIENTE_DA_CONVERSA: (state, payload) => {
    state.clienteDaConversa = payload || null
  },
  SET_CLIENTES_CITADOS: (state, payload) => {
    state.clientesCitados = Array.isArray(payload) ? payload : []
  },
  /**
   * ACRESCENTA à lista, sem repetir ([G-F4]).
   *
   * O merge mora aqui, e não na classe de API, porque quem tem a lista é o
   * slice: acumular do lado de lá exigiria LER a store para depois escrever
   * nela, e é assim que dois turnos concorrentes se sobrescrevem.
   *
   * A chave é o `idClienteApp` — o mesmo eixo que abre a conversa. Deduplicar
   * por código erraria no escritório 411, onde o mesmo `000124` existe em dois
   * cadastros (ADR-005): duas pessoas virariam uma linha só.
   */
  CITAR_CLIENTES: (state, payload) => {
    const novos = Array.isArray(payload) ? payload : []
    const atuais = state.clientesCitados || []
    const conhecidos = new Set(atuais.map((cliente) => cliente.idClienteApp))
    const acrescentar = []
    novos.forEach((cliente) => {
      if (!cliente || !cliente.idClienteApp) return
      if (conhecidos.has(cliente.idClienteApp)) return
      conhecidos.add(cliente.idClienteApp)
      acrescentar.push(cliente)
    })
    if (!acrescentar.length) return
    state.clientesCitados = [...atuais, ...acrescentar]
  },
  SET_ERRO_DO_TURNO: (state, payload) => {
    state.erroDoTurno = payload || ''
  },
  RESET_ERRO_DO_TURNO: (state) => {
    state.erroDoTurno = ''
  },
  SET_SELECTED_MODEL: (state, payload) => {
    state.selectedModel = payload
  },
  SET_THINKING_ENABLED: (state, payload) => {
    state.thinkingEnabled = !!payload
  },
  /**
   * A lista viva chegou — e é ela que diz qual modelo vale.
   *
   * ⚠️ A reconciliação é INCONDICIONAL. A guarda anterior ("só aplica o padrão
   * do servidor para quem ainda não escolheu") preservava justamente o caso
   * defeituoso: era o id SALVO que estava obsoleto. Quem tinha
   * `claude-sonnet-4-6` guardado — modelo que saiu de `/supported-models` —
   * mandava todo turno do drawer com ele e recebia 400 (Q7-A08 / K5).
   *
   * Uma escolha VÁLIDA continua intocada: é o que `modeloValidoOuPadrao`
   * garante, e lista vazia (rota falhou) preserva o que estava lá.
   *
   * Mutação PURA: quem reescreve o `localStorage` é a action `setSupportedModels`.
   */
  SET_SUPPORTED_MODELS: (state, payload) => {
    state.supportedModels = (payload && payload.models) || []
    state.supportedModelsLoaded = true
    state.selectedModel = modeloValidoOuPadrao(state.selectedModel, payload)
  },
}

export const actions = {
  ...toolPartsActions,
  ...hubEAgenteActions,
  ...deposito.depositoActions,
  abrirPainel: (context) => context.commit('SET_PAINEL_ABERTO', true),
  fecharPainel: (context) => context.commit('SET_PAINEL_ABERTO', false),
  // Lê o estado aqui, e não no componente: o componente que decidisse pelo
  // próprio `aberto` voltaria a ter uma segunda fonte de verdade.
  alternarPainel: (context) =>
    context.commit('SET_PAINEL_ABERTO', !context.state.painelAberto),
  setMessages: (context, payload) => context.commit('SET_MESSAGES', payload),
  addMessage: (context, payload) => context.commit('ADD_MESSAGE', payload),
  setChatLoading: (context, payload) => context.commit('SET_CHAT_LOADING', payload),
  addResponseTokens: (context, payload) => context.commit('ADD_RESPONSE_TOKENS', payload),
  addResponseToken: (context, payload) => context.commit('ADD_RESPONSE_TOKEN', payload),
  resetResponseTokens: (context) => context.commit('RESET_RESPONSE_TOKENS'),
  addThinkingStep: (context, payload) => context.commit('ADD_THINKING_STEP', payload),
  resetThinkingSteps: (context) => context.commit('RESET_THINKING_STEPS'),
  addToolCall: (context, payload) => context.commit('ADD_TOOL_CALL', payload),
  removeToolCall: (context, payload) => context.commit('REMOVE_TOOL_CALL', payload),
  resetToolCalls: (context) => context.commit('RESET_TOOL_CALLS'),
  setPendingConfirmation: (context, payload) =>
    context.commit('SET_PENDING_CONFIRMATION', payload),
  resetPendingConfirmation: (context) => context.commit('RESET_PENDING_CONFIRMATION'),
  setFiltroSugerido: (context, payload) =>
    context.commit('SET_FILTRO_SUGERIDO', payload),
  resetFiltroSugerido: (context) => context.commit('RESET_FILTRO_SUGERIDO'),
  setSimulacaoPendente: (context, payload) =>
    context.commit('SET_SIMULACAO_PENDENTE', payload),
  resetSimulacaoPendente: (context) =>
    context.commit('RESET_SIMULACAO_PENDENTE'),
  setRascunhoDaTela: (context, payload) =>
    context.commit('SET_RASCUNHO_DA_TELA', payload),
  limparRascunhoDaTela: (context) => context.commit('SET_RASCUNHO_DA_TELA', null),
  setRascunhoAnalisado: (context, payload) =>
    context.commit('SET_RASCUNHO_ANALISADO', payload),
  resetRascunhoAnalisado: (context) =>
    context.commit('RESET_RASCUNHO_ANALISADO'),
  setSelectedConversation: (context, payload) =>
    context.commit('SET_SELECTED_CONVERSATION', payload),
  // O nome `setConversations` é CONTRATO com o vizinho: o `carregar()` do
  // `apiConversations` despacha `${storeModule}/setConversations`. Renomear
  // aqui quebraria a lateral em silêncio.
  setConversations: (context, payload) =>
    context.commit('SET_CONVERSATIONS', payload),
  setConversationsLoading: (context, payload) =>
    context.commit('SET_CONVERSATIONS_LOADING', payload),
  setConversationsError: (context, payload) =>
    context.commit('SET_CONVERSATIONS_ERROR', payload),
  setClienteDaConversa: (context, payload) =>
    context.commit('SET_CLIENTE_DA_CONVERSA', payload),
  setClientesCitados: (context, payload) =>
    context.commit('SET_CLIENTES_CITADOS', payload),
  citarClientes: (context, payload) =>
    context.commit('CITAR_CLIENTES', payload),
  setErroDoTurno: (context, payload) =>
    context.commit('SET_ERRO_DO_TURNO', payload),
  resetErroDoTurno: (context) => context.commit('RESET_ERRO_DO_TURNO'),
  // Zera a conversa inteira — usado pelo "Nova conversa" do drawer.
  //
  // A LISTA da lateral não é tocada de propósito: ela é do profissional, não da
  // conversa que acabou de ser fechada. Limpá-la aqui faria "Nova conversa"
  // apagar o histórico da coluna até o próximo `listar()`.
  limparConversa: (context) => {
    context.commit('SET_MESSAGES', [])
    context.commit('SET_SELECTED_CONVERSATION', null)
    // A conversa nova nasce geral: manter o cliente anterior faria a próxima
    // pergunta sair vinculada a quem o consultor acabou de deixar.
    context.commit('SET_CLIENTE_DA_CONVERSA', null)
    context.commit('RESET_RESPONSE_TOKENS')
    context.commit('RESET_THINKING_STEPS')
    context.commit('RESET_TOOL_CALLS')
    context.commit('RESET_PENDING_CONFIRMATION')
    // A sugestão pertence à conversa que a produziu: mantê-la depois do "Nova
    // conversa" deixaria na tela um ajuste sem a explicação que o justificava.
    context.commit('RESET_RASCUNHO_ANALISADO')
    // ★ [G-R7] O RECORTE pela mesma razão, e com sintoma maior desde que ele
    // ganhou renderizador no thread do guia: sobrava um "Recorte sugerido" no
    // fim de uma conversa vazia, oferecendo aplicar um recorte que foi pedido
    // em OUTRA conversa.
    context.commit('RESET_FILTRO_SUGERIDO')
    context.commit('RESET_SIMULACAO_PENDENTE')
    // ★ [G-A6] E o erro do turno, que é o caso mais literal: a recusa diz
    // "comece uma conversa nova para continuar" — ela não pode sobreviver ao
    // clique que faz exatamente isso.
    context.commit('RESET_ERRO_DO_TURNO')
    // Os clientes citados são da conversa que os citou: sobrevivendo, o cartão
    // ofereceria abrir a conversa de um cliente que ninguém mencionou aqui.
    context.commit('SET_CLIENTES_CITADOS', [])
  },
  setSelectedModel: (context, payload) => {
    context.commit('SET_SELECTED_MODEL', payload)
    gravarLocalStorage(LS_MODELO, payload)
  },
  setThinkingEnabled: (context, payload) => {
    context.commit('SET_THINKING_ENABLED', payload)
    gravarLocalStorage(LS_THINKING, String(payload))
  },
  // Quem busca é a API, que persiste por esta action. ★ O id obsoleto vivia no
  // `localStorage`: sem esta reescrita ele volta no próximo carregamento da
  // página e o 400 se repete. A chave é a PRÓPRIA do drawer — a do chat do CRM
  // não é tocada.
  setSupportedModels: (context, payload) => {
    const escolhaAnterior = context.state.selectedModel
    context.commit('SET_SUPPORTED_MODELS', payload)
    const escolhaAtual = context.state.selectedModel
    if (escolhaAtual === escolhaAnterior) return
    gravarLocalStorage(LS_MODELO, escolhaAtual)
  },
  // Passthrough: o nome é contrato do `SendMessage` (`storeModule + '/loadSupportedModels'`).
  // Idempotente porque o composer despacha no `mounted`, e ele remonta a cada
  // troca de aba do Fee Fixo (a `key` do RouterView derruba a árvore).
  loadSupportedModels: (context) => {
    if (context.state.supportedModelsLoaded) return
    return apiSupportedModels.carregarPara({ storeModule: NAMESPACE })
  },
}
